/**
 * VFX burst presets (story `q-demo-vfx-pass`) — DATA, not code: each named effect is a
 * particle count, lifetime, speed, size ramp, colour ramp, gravity/drag and a spread shape,
 * drawn on one of four layers. The director (`director.ts`) maps a game-feedback cue to
 * one or more presets (`cues.ts`); the pool (`pool.ts`) integrates them; the renderer
 * (`renderer.ts`) draws each layer as ONE instanced draw (zero draws while a layer is idle).
 *
 * Presentation values only: nothing here is read by gameplay, and none of it is tuning a
 * player could exploit. Colours are linear RGB in [0, 1] (additive blending).
 *
 * Reserved hues (Art Direction Bible §3.7): the hostile telegraph red (#e8443a) is used
 * ONLY by the hostile wind-up and slam presets, the safe/heal green (#39c46a) ONLY by heal.
 */

/** The four draw layers, in draw order. `ring` is a flat ground quad; the rest billboard. */
export const VFX_LAYERS = ['ring', 'glow', 'spark', 'star'] as const;
export type VfxLayer = (typeof VFX_LAYERS)[number];

/** `column`: rises from a circle of radius `jitter` round the anchor (never over the body). */
export type Spread = 'sphere' | 'up' | 'flat' | 'still' | 'column';
export type Rgb = readonly [number, number, number];

export interface BurstPreset {
  layer: VfxLayer;
  count: number;
  /** Seconds, [min, max]. */
  life: readonly [number, number];
  /** m/s, [min, max] (× the spawn's scale). */
  speed: readonly [number, number];
  /** World size in metres at birth and at death (× the spawn's scale). */
  size: readonly [number, number];
  color: Rgb;
  color2?: Rgb;
  /** m/s² downward (negative floats up). */
  gravity: number;
  /** Velocity damping per second. */
  drag: number;
  spread: Spread;
  /** Spawn height above the anchor's ground, metres. */
  height: number;
  /** Spawn position jitter radius, metres (× scale). */
  jitter: number;
}

function hex(h: string): Rgb {
  const n = parseInt(h.replace('#', ''), 16);
  // sRGB → linear (approx. gamma 2.2) so the additive result reads as the authored hue.
  const lin = (c: number): number => Math.pow(c / 255, 2.2);
  return [lin((n >> 16) & 255), lin((n >> 8) & 255), lin(n & 255)];
}

const WHITE_HOT = hex('#fff4d6');
const EMBER = hex('#ff9a3c');
const HOSTILE = hex('#e8443a'); // Bible §3.7 reserved: hostile telegraph only
const HEAL = hex('#39c46a'); // Bible §3.7 reserved: safe/heal only
const SHIELD = hex('#9fd4ff');
const IMAGE = hex('#7fd8ff'); // the mirror-image sheen (mirrorImage.ts)
const GOLD = hex('#ffd36b');
const GOLD_PALE = hex('#fff1bf');
const VIOLET = hex('#b9a3ff');
const DUSK = hex('#5b4a7a');
const DUST = hex('#b89a74');
const BARK = hex('#a8743f');
const LEAF = hex('#9be36a');
const ORE = hex('#ffb86b');
const FIBRE = hex('#f2e6a8');

export const VFX_PRESETS = {
  // --- combat: blows ---------------------------------------------------------------
  hitSpark: {
    layer: 'spark',
    count: 10,
    life: [0.16, 0.3],
    speed: [3, 6],
    size: [0.169, 0.039],
    color: WHITE_HOT,
    color2: EMBER,
    gravity: 7,
    drag: 3,
    spread: 'sphere',
    height: 1.1,
    jitter: 0.08,
  },
  hitFlash: {
    layer: 'glow',
    count: 1,
    life: [0.12, 0.14],
    speed: [0, 0],
    size: [0.55, 1.15],
    color: WHITE_HOT,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.1,
    jitter: 0,
  },
  heavySpark: {
    layer: 'spark',
    count: 16,
    life: [0.2, 0.38],
    speed: [4, 8],
    size: [0.208, 0.039],
    color: WHITE_HOT,
    color2: EMBER,
    gravity: 8,
    drag: 3,
    spread: 'sphere',
    height: 1.1,
    jitter: 0.1,
  },
  heavyFlash: {
    layer: 'glow',
    count: 1,
    life: [0.16, 0.18],
    speed: [0, 0],
    size: [0.8, 1.7],
    color: WHITE_HOT,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.1,
    jitter: 0,
  },
  hurtSpark: {
    layer: 'spark',
    count: 8,
    life: [0.2, 0.32],
    speed: [2, 4],
    size: [0.156, 0.039],
    color: hex('#ffb0a0'),
    color2: hex('#c0392b'),
    gravity: 6,
    drag: 3,
    spread: 'sphere',
    height: 1.1,
    jitter: 0.1,
  },
  // --- abilities -------------------------------------------------------------------
  healMotes: {
    layer: 'star',
    count: 18,
    life: [0.8, 1.2],
    speed: [0.8, 1.6],
    size: [0.34, 0.1],
    color: hex('#9df5b8'),
    color2: HEAL,
    gravity: -0.6,
    drag: 1.2,
    spread: 'up',
    height: 0.3,
    jitter: 0.45,
  },
  healGlow: {
    layer: 'glow',
    count: 1,
    life: [0.7, 0.75],
    speed: [0, 0],
    size: [1.2, 2.4],
    color: HEAL,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.0,
    jitter: 0,
  },
  shieldShimmer: {
    layer: 'star',
    count: 20,
    life: [0.5, 0.8],
    speed: [1, 1.4],
    size: [0.24, 0.06],
    color: hex('#ffffff'),
    color2: SHIELD,
    gravity: -0.4,
    drag: 2,
    spread: 'flat',
    height: 1.0,
    jitter: 0.2,
  },
  shieldGlow: {
    layer: 'glow',
    count: 1,
    life: [0.8, 0.85],
    speed: [0, 0],
    size: [2.0, 2.6],
    color: SHIELD,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.0,
    jitter: 0,
  },
  decoyShimmer: {
    layer: 'star',
    count: 18,
    life: [0.5, 0.9],
    speed: [1.4, 2.8],
    size: [0.255, 0.06],
    color: hex('#e8fbff'),
    color2: IMAGE,
    gravity: 0,
    drag: 2.5,
    spread: 'sphere',
    height: 1.0,
    jitter: 0.25,
  },
  decoyGlow: {
    layer: 'glow',
    count: 1,
    life: [0.45, 0.5],
    speed: [0, 0],
    size: [1.0, 1.9],
    color: IMAGE,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.0,
    jitter: 0,
  },
  buffMotes: {
    layer: 'star',
    count: 12,
    life: [0.7, 1.0],
    speed: [0.9, 1.6],
    size: [0.24, 0.06],
    color: GOLD_PALE,
    color2: GOLD,
    gravity: -0.4,
    drag: 1.2,
    spread: 'up',
    height: 0.2,
    jitter: 0.4,
  },
  controlRing: {
    layer: 'ring',
    count: 1,
    life: [0.45, 0.5],
    speed: [0, 0],
    size: [0.6, 5.0],
    color: GOLD,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.06,
    jitter: 0,
  },
  // --- Dodge -----------------------------------------------------------------------
  dodgeTrail: {
    layer: 'glow',
    count: 1,
    life: [0.28, 0.32],
    speed: [0, 0],
    size: [0.6, 0.2],
    color: hex('#cfe8ff'),
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.9,
    jitter: 0.04,
  },
  dodgeKick: {
    layer: 'ring',
    count: 1,
    life: [0.3, 0.32],
    speed: [0, 0],
    size: [0.4, 1.8],
    color: hex('#cfe8ff'),
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.05,
    jitter: 0,
  },
  // --- hostile wind-up + slam (the Sentinel) ----------------------------------------
  windupPulse: {
    layer: 'ring',
    count: 1,
    life: [0.4, 0.42],
    speed: [0, 0],
    size: [0.1, 1.0],
    color: HOSTILE,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.07,
    jitter: 0,
  },
  slamWave: {
    layer: 'ring',
    count: 1,
    life: [0.42, 0.45],
    speed: [0, 0],
    size: [0.3, 2.3],
    color: hex('#ffd0b0'),
    color2: HOSTILE,
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.08,
    jitter: 0,
  },
  slamDust: {
    layer: 'glow',
    count: 10,
    life: [0.5, 0.8],
    speed: [2, 4],
    size: [0.5, 1.1],
    color: DUST,
    color2: [0, 0, 0],
    gravity: 0.5,
    drag: 3,
    spread: 'flat',
    height: 0.2,
    jitter: 0.3,
  },
  slamSpark: {
    layer: 'spark',
    count: 12,
    life: [0.25, 0.4],
    speed: [4, 7],
    size: [0.182, 0.039],
    color: WHITE_HOT,
    color2: HOSTILE,
    gravity: 9,
    drag: 2,
    spread: 'flat',
    height: 0.3,
    jitter: 0.2,
  },
  // --- gathering (per node family) --------------------------------------------------
  strikeOre: {
    layer: 'spark',
    count: 14,
    life: [0.3, 0.55],
    speed: [2.5, 5],
    size: [0.18, 0.05],
    color: WHITE_HOT,
    color2: ORE,
    gravity: 9,
    drag: 2,
    spread: 'sphere',
    height: 0.6,
    jitter: 0.1,
  },
  strikeWood: {
    layer: 'spark',
    count: 12,
    life: [0.45, 0.7],
    speed: [1.5, 3],
    size: [0.2, 0.12],
    color: BARK,
    color2: hex('#6b4423'),
    gravity: 8,
    drag: 1.5,
    spread: 'flat',
    height: 0.8,
    jitter: 0.1,
  },
  strikeHerb: {
    layer: 'star',
    count: 12,
    life: [0.6, 0.9],
    speed: [0.8, 1.6],
    size: [0.24, 0.08],
    color: LEAF,
    color2: hex('#4f8a3a'),
    gravity: 1.5,
    drag: 1.5,
    spread: 'up',
    height: 0.3,
    jitter: 0.2,
  },
  strikeFibre: {
    layer: 'star',
    count: 8,
    life: [0.4, 0.7],
    speed: [0.8, 1.6],
    size: [0.18, 0.06],
    color: FIBRE,
    color2: hex('#a39a63'),
    gravity: 1,
    drag: 1.5,
    spread: 'up',
    height: 0.3,
    jitter: 0.2,
  },
  strikeDust: {
    layer: 'glow',
    count: 4,
    life: [0.4, 0.6],
    speed: [0.6, 1.2],
    size: [0.3, 0.7],
    color: DUST,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 2,
    spread: 'flat',
    height: 0.2,
    jitter: 0.2,
  },
  gatherSparkle: {
    layer: 'star',
    count: 6,
    life: [0.6, 1.0],
    speed: [1, 2],
    size: [0.22, 0.05],
    color: GOLD_PALE,
    color2: GOLD,
    gravity: -0.3,
    drag: 1.5,
    spread: 'up',
    height: 0.5,
    jitter: 0.3,
  },
  // --- crafting, loot, milestones ---------------------------------------------------
  craftSparkle: {
    layer: 'star',
    count: 18,
    life: [0.7, 1.1],
    speed: [1.2, 2.4],
    size: [0.3, 0.06],
    color: GOLD_PALE,
    color2: GOLD,
    gravity: -0.2,
    drag: 1.6,
    spread: 'sphere',
    height: 1.3,
    jitter: 0.15,
  },
  craftGlow: {
    layer: 'glow',
    count: 1,
    life: [0.4, 0.45],
    speed: [0, 0],
    size: [0.6, 1.4],
    color: GOLD,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 1.3,
    jitter: 0,
  },
  lootBeam: {
    layer: 'star',
    count: 14,
    life: [0.8, 1.2],
    speed: [1.5, 2.6],
    size: [0.27, 0.075],
    color: GOLD_PALE,
    color2: GOLD,
    gravity: -0.2,
    drag: 0.8,
    spread: 'up',
    height: 0.2,
    jitter: 0.15,
  },
  lootRare: {
    layer: 'glow',
    count: 3,
    life: [0.7, 0.9],
    speed: [0.6, 1.2],
    size: [0.7, 1.3],
    color: VIOLET,
    color2: [0, 0, 0],
    gravity: -0.6,
    drag: 1,
    spread: 'up',
    height: 0.3,
    jitter: 0.1,
  },
  questBurst: {
    layer: 'star',
    count: 28,
    life: [0.9, 1.4],
    speed: [2, 3.6],
    size: [0.33, 0.075],
    color: GOLD_PALE,
    color2: GOLD,
    gravity: 0.6,
    drag: 1.4,
    spread: 'sphere',
    height: 1.6,
    jitter: 0.1,
  },
  questRing: {
    layer: 'ring',
    count: 1,
    life: [0.6, 0.65],
    speed: [0, 0],
    size: [0.5, 4.5],
    color: GOLD,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.06,
    jitter: 0,
  },
  levelColumn: {
    layer: 'star',
    count: 30,
    life: [1.0, 1.5],
    speed: [2.2, 4],
    size: [0.33, 0.075],
    color: hex('#ffffff'),
    color2: GOLD,
    gravity: -0.4,
    drag: 0.6,
    spread: 'column',
    height: 0.1,
    jitter: 0.75,
  },
  levelGlow: {
    layer: 'glow',
    count: 2,
    life: [0.7, 0.8],
    speed: [0, 0.3],
    size: [1.2, 2.6],
    color: GOLD,
    color2: [0, 0, 0],
    gravity: -1,
    drag: 0,
    spread: 'up',
    height: 1.0,
    jitter: 0,
  },
  levelRing: {
    layer: 'ring',
    count: 1,
    life: [0.7, 0.75],
    speed: [0, 0],
    size: [0.5, 5.5],
    color: GOLD_PALE,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.06,
    jitter: 0,
  },
  // --- death / respawn --------------------------------------------------------------
  deathWisps: {
    layer: 'glow',
    count: 12,
    life: [0.9, 1.4],
    speed: [0.4, 1.0],
    size: [0.45, 0.9],
    color: VIOLET,
    color2: DUSK,
    gravity: -0.8,
    drag: 1,
    spread: 'up',
    height: 0.5,
    jitter: 0.4,
  },
  deathMotes: {
    layer: 'star',
    count: 10,
    life: [0.8, 1.2],
    speed: [0.8, 1.6],
    size: [0.21, 0.045],
    color: hex('#e2d8ff'),
    color2: VIOLET,
    gravity: -0.5,
    drag: 1,
    spread: 'up',
    height: 0.6,
    jitter: 0.4,
  },
  respawnColumn: {
    layer: 'star',
    count: 24,
    life: [0.9, 1.3],
    speed: [2, 3.4],
    size: [0.27, 0.06],
    color: hex('#e8fbff'),
    color2: IMAGE,
    gravity: -0.2,
    drag: 0.6,
    spread: 'column',
    height: 0.1,
    jitter: 0.35,
  },
  respawnRing: {
    layer: 'ring',
    count: 1,
    life: [0.6, 0.65],
    speed: [0, 0],
    size: [0.4, 3.2],
    color: IMAGE,
    color2: [0, 0, 0],
    gravity: 0,
    drag: 0,
    spread: 'still',
    height: 0.06,
    jitter: 0,
  },
} as const satisfies Record<string, BurstPreset>;

export type VfxPresetId = keyof typeof VFX_PRESETS;

export function presetFor(id: VfxPresetId): BurstPreset {
  return VFX_PRESETS[id];
}
